import { preparePhoneNumberForSubmit } from '@helpers/phone';
import { translateApiError } from '@helpers/api';
import { specialHoursForSubmit } from '@helpers/companies';
import { SOCIAL_BULK_FIELDS } from '@utils/socialLinkUtils';

const FIELD_NAME_TO_ENUM = {
    locationName: 'LocationName',
    status: 'Status',
    phoneNumber: 'PhoneNumber',
    phoneExtension: 'PhoneExtension',
    email: 'Email',
    website: 'Website',
    workingHours: 'WorkingHours',
    specialHours: 'SpecialHours',
    shortDescription: 'ShortDescription',
    mediumDescription: 'MediumDescription',
    longDescription: 'LongDescription',
    openingDate: 'OpeningDate',
    services: 'Services',
    paymentMethods: 'PaymentMethods',
    logoUrl: 'LogoUrl',
    coverUrl: 'CoverUrl',
    ...Object.fromEntries(SOCIAL_BULK_FIELDS.map(({ fieldValue, enumName }) => [fieldValue, enumName])),
};

const SOCIAL_FIELD_TO_KEY = Object.fromEntries(SOCIAL_BULK_FIELDS.map(({ fieldValue, key }) => [fieldValue, key]));

export const buildBulkUpdateRequest = (locationIds, field, value) => {
    const location = { companyId: 0 };
    const fields = [];

    switch (field) {
        case 'phoneNumber':
            location.phoneNumber = preparePhoneNumberForSubmit(value.number);
            location.phoneExtension = value.extension || '';
            fields.push('PhoneNumber', 'PhoneExtension');
            break;

        case 'services':
            location.services = value.map(s => s.name).filter(n => n.trim());
            fields.push('Services');
            break;

        case 'paymentMethods':
            location.paymentMethods = value;
            fields.push('PaymentMethods');
            break;

        case 'workingHours':
            location.workingHours = value;
            location.workingHoursEnabled = value && value.length > 0;
            fields.push('WorkingHours', 'WorkingHoursEnabled');
            break;

        case 'specialHours':
            location.specialHours = specialHoursForSubmit(value);
            fields.push('SpecialHours');
            break;

        case 'status':
            location.status = value;
            fields.push(FIELD_NAME_TO_ENUM[field]);
            break;

        case 'openingDate':
            location.openingDate = value;
            fields.push(FIELD_NAME_TO_ENUM[field]);
            break;

        case 'logoUrl':
        case 'coverUrl':
            location[field] = value;
            fields.push(FIELD_NAME_TO_ENUM[field]);
            break;

        case 'email':
        case 'website':
        case 'locationName':
        case 'shortDescription':
        case 'mediumDescription':
        case 'longDescription':
            location[field] = value;
            fields.push(FIELD_NAME_TO_ENUM[field]);
            break;

        default:
            if (SOCIAL_FIELD_TO_KEY[field]) {
                location.socialLinks = { [SOCIAL_FIELD_TO_KEY[field]]: value?.trim() || null };
                fields.push(FIELD_NAME_TO_ENUM[field]);
            }
            break;
    }

    return {
        locationIds,
        fields,
        location,
    };
};

const GENERIC_ERROR_CODE = 'validation';
const UNRETRYABLE_ERROR_CODES = ['forbid', 'location_not_found'];

const failureReason = (item, t) => {
    if (!item.errorCode) return t('apiErrors.something_wrong');
    return item.errorCode === GENERIC_ERROR_CODE ? item.error : translateApiError(item.errorCode, item.error);
};

export const bulkUpdateOutcome = (result, locations, t) => {
    const labels = new Map(locations.map(location => [location.id, location.label]));
    return {
        complete: result.successCount === result.totalLocations,
        updatedAny: result.successCount > 0,
        total: result.totalLocations,
        failed: (result.results || [])
            .filter(item => !item.success)
            .map(item => ({
                locationId: item.locationId,
                name: labels.get(item.locationId) || item.locationName,
                reason: failureReason(item, t),
                retryable: !UNRETRYABLE_ERROR_CODES.includes(item.errorCode),
            })),
    };
};
